/**
 * The solo questions, as data: which mode, Sprint's goal and target, and how
 * hard. One shape for the two pickers that ask them, the solo New game
 * picker and the setup of a round on one device (`docs/decisions/0059`), so
 * a round offers exactly the choices a solo run does.
 */
import {
  DEFAULT_HARDNESS,
  SPRINT_DEFAULT_LINE_GOAL,
  buildMode,
  describeModeConfig,
} from '@interlock/modes';
import type { Hardness, ModeConfig } from '@interlock/modes';

export type PickedMode = 'sprint' | 'survival';
export type GoalKind = 'lines' | 'time';

export interface ModeChoices {
  readonly mode: PickedMode;
  readonly goalKind: GoalKind;
  /** Sprint's line target. */
  readonly lines: number;
  /** Sprint's time limit, in whole minutes. */
  readonly timeMin: number;
  readonly hardness: Hardness;
}

/** What both pickers open on: Sprint to the default line goal, at the default hardness. */
export function defaultModeChoices(): ModeChoices {
  return {
    mode: 'sprint',
    goalKind: 'lines',
    lines: SPRINT_DEFAULT_LINE_GOAL,
    timeMin: 3,
    hardness: DEFAULT_HARDNESS,
  };
}

/** The configuration the choices describe. Survival has no goal. */
export function configFromChoices(choices: ModeChoices): ModeConfig {
  if (choices.mode === 'survival') return { modeId: 'survival', hardness: choices.hardness };
  return {
    modeId: 'sprint',
    hardness: choices.hardness,
    goal:
      choices.goalKind === 'lines'
        ? { kind: 'lines', lines: choices.lines }
        : { kind: 'time', ms: choices.timeMin * 60_000 },
  };
}

/** "Sprint · Hard · 40 lines": the mode, then its configuration as the HUD will show it. */
export function describeChoice(config: ModeConfig): string {
  return `${buildMode(config).copy.name} · ${describeModeConfig(config)}`;
}
